// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Image, Pressable, StyleSheet, TextInput, Platform, ScrollView } from 'react-native';
import { Redirect, useRouter, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { Text, View } from '@/components/Themed';
import { useUser } from './user-context';
import { useI18n } from '@/i18n';
import LoginHero from '@/assets/images/login-hero.png';
import { AuthStatusCard } from '@/components/AuthStatusCard';
import { useRegisterUser } from '@/app/auth/useRegisterUser';
import { getApiBaseUrl } from '@/services/apiBaseUrl';

export default function RegisterScreen() {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const { user } = useUser();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { t } = useI18n();

  const [forename, setForename] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { status, error, loading, socketState, sendCreateRequest } = useRegisterUser({
    apiBaseUrl,
    isFocused,
    forename,
    email,
    password,
    onReset: () => {
      setForename('');
      setEmail('');
      setPassword('');
    },
  });

  if (!isNavigationReady) return null;
  if (user) return <Redirect href="/tournaments" />;

  return (
    <ScrollView contentContainerStyle={styles.container} style={styles.scroll}>
      <Image source={LoginHero} style={styles.hero} resizeMode="contain" />
      <Text style={styles.title}>{t('register.title')}</Text>
      <AuthStatusCard
        title={t('register.cardTitle')}
        status={status}
        error={error}
        buttonLabel={loading ? t('register.creating') : t('register.createCta')}
        onPress={sendCreateRequest}
        disabled={loading}
      >
        <Text style={styles.label}>{t('register.forenameLabel')}</Text>
        <TextInput
          value={forename}
          onChangeText={setForename}
          placeholder={t('register.forenamePlaceholder')}
          style={styles.input}
          autoCapitalize="words"
          placeholderTextColor="#5b6b8f"
        />
        <Text style={styles.label}>{t('register.emailLabel')}</Text>
        <TextInput
          value={email}
          onChangeText={setEmail}
          placeholder={t('register.emailPlaceholder')}
          style={styles.input}
          keyboardType="email-address"
          autoCapitalize="none"
          placeholderTextColor="#5b6b8f"
        />
        <Text style={styles.label}>{t('register.passwordLabel')}</Text>
        <TextInput
          value={password}
          onChangeText={setPassword}
          placeholder={t('register.passwordPlaceholder')}
          style={styles.input}
          secureTextEntry
          placeholderTextColor="#5b6b8f"
        />
        <Text style={styles.connection}>
          {t('register.connectionLabel', { status: socketState === 'connected' ? t('register.connectionUp') : t('register.connectionDown') })}
        </Text>
        <Pressable style={styles.link} onPress={() => router.replace('/login')}>
          <Text style={styles.linkText}>{t('register.backToLogin')}</Text>
        </Pressable>
      </AuthStatusCard>
    </ScrollView>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 4px 10px rgba(0,0,0,0.25)' },
  default: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 4,
  },
});

const styles = StyleSheet.create({
  container: {
    padding: 16,
    alignItems: 'center',
    gap: 12,
  },
  scroll: { flex: 1, backgroundColor: '#040814' },
  hero: {
    width: '100%',
    height: 200,
    marginTop: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: '800',
    color: '#e5edff',
  },
  label: {
    color: '#d5e1ff',
    fontWeight: '700',
    marginBottom: 6,
    marginTop: 10,
  },
  input: {
    borderWidth: 1,
    borderColor: '#24346b',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: '#e5edff',
    backgroundColor: '#101a33',
  },
  connection: {
    color: '#94a3b8',
    fontSize: 12,
    marginTop: 12,
  },
  link: {
    marginTop: 12,
    alignItems: 'center',
  },
  linkText: {
    color: '#9dd0ff',
    fontWeight: '700',
  },
});




function generateRequestId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as { randomUUID?: () => string }).randomUUID?.() ?? `req-${Date.now()}`;
  }
  const random = Math.random().toString(36).slice(2, 8);
  return `req-${Date.now()}-${random}`;
}
